import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  Mail,
  Calendar,
  CreditCard,
  Search,
  LogOut,
  Sparkles,
} from "lucide-react";
import { useAuth } from "./AuthContext";
import SEO from "./SEO";

/**
 * AdminPageShell — Unified chrome for every /admin/* page.
 *
 * Renders:
 *   - Header (eyebrow label · H1 · subtitle · user pill top-right)
 *   - Sticky tab navigation (6 modules: overview · blog · mssante · demos · billing · seo)
 *   - The page-specific content via {children}
 *
 * Each admin page is responsible for its own data loading; the shell is presentation-only.
 */
const TABS = [
  { key: "overview", label: "Vue générale", to: "/admin", icon: LayoutDashboard },
  { key: "studio", label: "Studio", to: "/admin/studio", icon: Sparkles },
  { key: "blog", label: "Blog", to: "/admin/blog", icon: FileText },
  { key: "demandes-mss", label: "MSSanté", to: "/admin/demandes-mss", icon: Mail },
  { key: "demos", label: "Démos", to: "/admin/demos", icon: Calendar },
  { key: "billing", label: "Facturation", to: "/admin/facturation", icon: CreditCard },
  { key: "seo", label: "SEO", to: "/admin/seo", icon: Search },
];

export default function AdminPageShell({
  title = "Tableau de bord",
  subtitle = "Vue d'ensemble de votre espace MetaConnect",
  eyebrow = "Espace admin",
  tab = "overview",
  seoTitle,
  seoCanonical,
  children,
}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Allow callers to override active tab; otherwise auto-detect from URL.
  const activeKey =
    (tab === "mssante" ? "demandes-mss" : tab) ||
    TABS.find((t) => location.pathname === t.to || location.pathname.startsWith(t.to + "/"))?.key ||
    "overview";

  const initials = (user?.email || "?")
    .split("@")[0]
    .split(".")
    .map((s) => s.charAt(0).toUpperCase())
    .slice(0, 2)
    .join("");

  const onLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[var(--mc-navy-950)] hex-grid" data-testid="admin-page-shell">
      <SEO
        title={seoTitle || `${title} — Admin MetaConnect`}
        canonical={seoCanonical || "/admin"}
        noIndex
      />

      {/* HEADER */}
      <header className="pt-10 pb-6 px-5 lg:px-8">
        <div className="max-w-7xl mx-auto flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div
              className="text-[10.5px] uppercase tracking-[0.24em] text-[var(--mc-muted)]"
              data-testid="admin-eyebrow"
            >
              {eyebrow}
            </div>
            <h1
              className="font-display text-[#0F2B5B] text-3xl lg:text-4xl mt-1.5 leading-tight"
              data-testid="admin-h1"
            >
              {title}
            </h1>
            {subtitle && (
              <p className="mt-1.5 text-[13.5px] text-[var(--mc-muted)]">{subtitle}</p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <div
              className="inline-flex items-center gap-2.5 pl-1 pr-3.5 py-1 rounded-full border border-[#E2E8F0] bg-white"
              data-testid="admin-user-pill"
            >
              <span className="grid place-items-center h-7 w-7 rounded-full bg-[#0F2B5B] text-white text-[11px] font-semibold uppercase shrink-0">
                {initials || "?"}
              </span>
              <span className="text-[12px] text-[#475569] truncate max-w-[180px]">
                {user?.email || "—"}
              </span>
            </div>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              title="Déconnexion"
              className="grid place-items-center h-9 w-9 rounded-full border border-[#E2E8F0] bg-white text-[#475569] hover:text-[#0F2B5B] hover:border-[#0EA5E9] transition"
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </header>

      {/* TAB NAV */}
      <nav
        className="px-5 lg:px-8 border-b border-[rgba(127,179,255,0.14)] sticky top-0 z-30 bg-[rgba(10,26,51,0.85)] backdrop-blur"
        data-testid="admin-tabs-nav"
      >
        <div className="max-w-7xl mx-auto flex items-center gap-1 overflow-x-auto no-scrollbar">
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = t.key === activeKey;
            return (
              <Link
                key={t.key}
                to={t.to}
                data-testid={`admin-tab-${t.key}`}
                aria-current={isActive ? "page" : undefined}
                className={`relative inline-flex items-center gap-2 px-4 py-3.5 text-[13px] font-medium whitespace-nowrap transition ${
                  isActive
                    ? "text-white"
                    : "text-white/60 hover:text-white"
                }`}
              >
                <Icon size={14} />
                {t.label}
                {isActive && (
                  <span className="absolute bottom-0 left-3 right-3 h-[2px] bg-[#4FC3F7] rounded-full" />
                )}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* PAGE CONTENT */}
      <main className="px-5 lg:px-8 py-8 pb-20">
        <div className="max-w-7xl mx-auto">{children}</div>
      </main>
    </div>
  );
}
